
import { useEffect, useRef, useState } from 'react';

const educationItems = [
  {
    school: 'The University of Texas',
    images: [
      { src: '/images/ut.png', alt: 'The University of Texas logo' },
      { src: '/images/longhorn.png', alt: 'Longhorn logo' },
    ],
    description: 'Masters of Articial Intelligence, expected graduation December 2027.',
  },
  {
    school: 'University of Southern California',
    images: [
      { src: '/images/usc.png', alt: 'University of Southern California logo' },
      { src: '/images/trojan.png', alt: 'Trojan logo' },
    ],
    description: 'Bachelors of Science in Computer Science, graduated May 2024.',
  },
];

const experienceItems = [
  {
    role: 'Software Engineer',
    company: 'Oracle',
    timeframe: 'September 2024 - Present',
    description: `Developed and maintain a microservice that calls a third-party endpoint to synchronize client information with our database before certain actions are performed. The frontend was built in Angular and PHP, where users can configure the third-party endpoint used during synchronization. The backend was written in Java and communicated with tables I created in the Oracle database.

I Use AI tools such as Codex to automate different aspects of the job. For example, I created a Codex skill that parses oncall alerts, determines cause through grafana dashboards and kubernetes logs, parses runbook and past slack messages to find viable solutions and gives me a summary of everything that was found + next best steps.`,
  },
  {
    role: 'Software Engineer Intern',
    company: 'Oracle',
    timeframe: 'May 2023 - August 2023',
    description: 'Integrated Oracle software into the backend of an international third party delivery platform with over 61 million users to connect restaurants to the delivery platform through three major functionalities: restaurants uploading a menu to the platform, sending orders from the platform to the restaurant and updating the availability of items in a restaurant on the platform.',
  },
  {
    role: 'Course Producer for Networking Protocol Course',
    company: 'USC',
    timeframe: 'December 2024 - May 2024',
    description: 'I held office hours for students to come for assistance with the course material and projects. The projects were C++ implementations of various networking protocols such as TCP, UDP and traceroute.',
  },
  {
    role: 'Software Developer Intern',
    company: 'SeeUnity - Anaqua',
    timeframe: 'May 2022 - August 2022',
    description: 'Designed a diagnostic app for the support team to diagnose problems for customers by creating a report of all the company’s software running on the client’s system. The diagnostic app parsed through three different logs and exported entries from a specific range of time to locate various errors, track the progress of data migrations and monitor the status of the server.',
  },
];

const projectItems = [
  {
    title: 'Vision-Language Models for Racing Scenes',
    area: 'Advanced Deep Learning',
    description: 'Built data pipelines that turn SuperTuxKart scene annotations into visual questions, answers, and image captions. Implemented a CLIP-style model that aligns image and text embeddings, alongside a LoRA fine-tuning pipeline for visual question answering.',
    technologies: 'PyTorch · Hugging Face · CLIP · LoRA',
  },
  {
    title: 'Language Models for Unit Conversion',
    area: 'Advanced Deep Learning',
    description: 'Developed a unit-conversion pipeline with SmolLM2, combining example-based reasoning prompts, supervised fine-tuning, and rejection-sampling fine-tuning. Generated candidate solutions, filtered them for correct answers, and used the resulting reasoning examples to train LoRA adapters.',
    technologies: 'Python · Hugging Face · PEFT · LoRA',
  },
  {
    title: 'Autoregressive Image Generation',
    area: 'Advanced Deep Learning',
    description: 'Implemented a patch autoencoder and Binary Spherical Quantization to represent racing images as discrete tokens. Built a causal transformer that predicts the next image token and generates new images through sequential sampling.',
    technologies: 'PyTorch · Transformers · Autoencoders · Quantization',
  },
  {
    title: 'Memory-Efficient Neural Networks',
    area: 'Advanced Deep Learning',
    description: 'Implemented half-precision layers, 4-bit weight quantization, LoRA, and QLoRA for a deep residual network. Combined frozen base weights with trainable adapters to support fine-tuning while limiting the memory needed for model weights and gradients.',
    technologies: 'PyTorch · FP16 · 4-bit Quantization · QLoRA',
  },
  {
    title: 'Visual Perception & Driving Planners',
    area: 'Deep Learning',
    description: 'Built convolutional models for image classification, road segmentation, and depth prediction in SuperTuxKart. Developed MLP, transformer, and CNN planners that predict driving waypoints from track boundaries or camera images.',
    technologies: 'PyTorch · Computer Vision · CNNs · Transformers',
  },
  {
    title: 'Sentiment Analysis',
    area: 'Natural Language Processing',
    description: 'Implemented perceptron and logistic-regression sentiment classifiers with unigram and bigram features, then built a deep averaging network using pretrained word embeddings. The neural model averages word representations and classifies text as positive or negative.',
    technologies: 'Python · NumPy · PyTorch · Word Embeddings',
  },
  {
    title: 'Bandit Learning & Dynamic Programming',
    area: 'Reinforcement Learning',
    description: 'Implemented epsilon-greedy action selection with sample-average and constant-step-size updates for multi-armed bandits. Built iterative policy evaluation and value iteration to estimate state values and derive greedy policies from environment transition models.',
    technologies: 'Python · NumPy · Gymnasium · Reinforcement Learning',
  },
];

function ScrollCue({ nextSectionId }) {
  return (
    <a className="resume-scroll-cue" href={`#${nextSectionId}`} aria-label="Scroll for more">
      <span className="resume-scroll-cue__text">Scroll for more</span>
      <span className="resume-scroll-cue__arrow" aria-hidden="true">
        &#8595;
      </span>
    </a>
  );
}

export default function ResumeScroller() {
  const educationRef = useRef(null);
  const experienceRef = useRef(null);
  const [educationVisible, setEducationVisible] = useState(false);
  const [experienceVisible, setExperienceVisible] = useState(false);

  useEffect(() => {
    const section = educationRef.current;

    if (!section) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setEducationVisible(entry.isIntersecting);
      },
      {
        threshold: 0.25,
      }
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const section = experienceRef.current;

    if (!section) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setExperienceVisible(entry.isIntersecting);
      },
      {
        threshold: 0.2,
      }
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <main className="resume-scroller">
      <section id="home" className="resume-section resume-hero">
        <div className="resume-section__backdrop resume-section__backdrop--hero"></div>
        <div className="resume-hero__inner">
          <h1 className="resume-name">Cray Carlton</h1>
          <div className="contact-card">
            <p className="contact-card__item">Email: cray.carlton7@gmail.com</p>
            <p className="contact-card__item">Phone: (970) 829-2528</p>
            <p className="contact-card__item">
              LinkedIn:{' '}
              <a
                className="contact-card__link"
                href="https://www.linkedin.com/in/cray-carlton"
                target="_blank"
                rel="noreferrer"
              >
                www.linkedin.com/in/cray-carlton
              </a>
            </p>
          </div>
        </div>
        <ScrollCue nextSectionId="education" />
      </section>
      <section
        id="education"
        ref={educationRef}
        className={`resume-section education-section${educationVisible ? ' is-visible' : ''}`}
      >
        <div className="resume-section__backdrop resume-section__backdrop--education"></div>
        <div className="education-panel">
          <div className="section-heading">
            <h2 className="section-heading__title">Education</h2>
          </div>
          <div className="education-grid">
            {educationItems.map((item) => (
              <article className="education-card" key={item.school}>
                <div className="education-card__image-wrap">
                  {item.images.map((image) => (
                    <img
                      className="education-card__image"
                      key={image.src}
                      src={image.src}
                      alt={image.alt}
                    />
                  ))}
                </div>
                <div className="education-card__content">
                  <h3 className="education-card__title">{item.school}</h3>
                  <p className="education-card__description">{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
        <ScrollCue nextSectionId="experience" />
      </section>
      <section
        id="experience"
        ref={experienceRef}
        className={`resume-section experience-section${experienceVisible ? ' is-visible' : ''}`}
      >
        <div className="resume-section__backdrop resume-section__backdrop--experience"></div>
        <div className="experience-panel">
          <div className="section-heading">
            <h2 className="section-heading__title">Experience</h2>
          </div>
          <div className="experience-list">
            {experienceItems.map((item) => (
              <article className="experience-card" key={`${item.role}-${item.company}`}>
                <div className="experience-card__header">
                  <div>
                    <h3 className="experience-card__title">{item.role}</h3>
                    <p className="experience-card__company">{item.company}</p>
                  </div>
                  <p className="experience-card__timeframe">{item.timeframe}</p>
                </div>
                <p className="experience-card__description">{item.description}</p>
              </article>
            ))}
          </div>
        </div>
        <ScrollCue nextSectionId="other-projects" />
      </section>
      <section id="other-projects" className="resume-section projects-section" aria-labelledby="other-projects-title">
        <div className="resume-section__backdrop resume-section__backdrop--education"></div>
        <div className="projects-panel">
          <div className="section-heading">
            <p className="section-heading__eyebrow">UT Austin Coursework</p>
            <h2 id="other-projects-title" className="section-heading__title">Other Projects</h2>
            <p className="projects-intro">Selected projects from my graduate studies in artificial intelligence at The University of Texas at Austin.</p>
          </div>
          <div className="projects-grid">
            {projectItems.map((project) => (
              <article className="project-card" key={project.title}>
                <p className="project-card__area">{project.area}</p>
                <h3 className="experience-card__title">{project.title}</h3>
                <p className="experience-card__description">{project.description}</p>
                <p className="project-card__technologies">{project.technologies}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
